import React, { useState, useMemo } from 'react';
import {
  Box,
  Button,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Input,
  Select,
  Badge,
  IconButton,
  Collapse,
  Text,
  VStack,
  HStack,
  Heading,
  Spinner,
  Alert,
  AlertIcon,
  Flex,
  Spacer,
  useDisclosure,
  Container,
  Card,
  CardBody,
  Divider,
  Tooltip,
  SimpleGrid,
  Tag,
  TagLabel,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalCloseButton,
  ModalBody,
  ModalFooter,
  useToast
} from '@chakra-ui/react';
import { 
  ChevronDownIcon, 
  ChevronUpIcon, 
  EditIcon, 
  DeleteIcon, 
  ChatIcon, 
  AddIcon,
  ViewIcon,
  SearchIcon
} from '@chakra-ui/icons';
import { useFindMany, useUser, useAction } from '@gadgetinc/react';
import { Link, useNavigate } from 'react-router-dom';
import { api } from '../api';
import { hasRole, normalizeRoles } from '../lib/roleUtils';

const VMAgentsManagement = () => {
  const navigate = useNavigate();
  const toast = useToast();
  
  // Current user and roles
  const user = useUser(api);
  const userRoles = user?.roles || [];
  const isSystemAdmin = userRoles.includes('system-admin');
  const isAdmin = hasRole(user, 'admin');
  
  // State for filtering and search
  const [searchTerm, setSearchTerm] = useState('');
  const [institutionFilter, setInstitutionFilter] = useState('all');
  const [expandedRows, setExpandedRows] = useState(new Set());
  
  // Fetch agent configurations
  const [{ data: agents, fetching, error }, refetch] = useFindMany(api.agentConfiguration, {
    select: {
      id: true,
      name: true, 
      description: true,
      isActive: true,
      version: true,
      instructions: {
        markdown: true
      },
      enabledTools: true,
      modelSettings: true,
      capabilities: true,
      createdAt: true,
      createdBy: {
        id: true,
        firstName: true,
        lastName: true,
        email: true,
        institution: true
      },
      lastModifiedBy: {
        id: true,
        firstName: true,
        lastName: true
      }
    }
  });

  // Delete action
  const [{ fetching: deleting }, deleteAgent] = useAction(api.agentConfiguration.delete);

  // Get unique institutions for filter
  const institutions = useMemo(() => {
    if (!agents) return [];
    const institutionSet = new Set();
    agents.forEach(agent => {
      if (agent.createdBy?.institution) {
        institutionSet.add(agent.createdBy.institution);
      }
    });
    return Array.from(institutionSet).sort();
  }, [agents]);

  // Filtered agents based on search and institution
  const filteredAgents = useMemo(() => {
    if (!agents) return [];
    
    return agents.filter(agent => {
      const matchesSearch = !searchTerm || 
        agent.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        agent.description?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        agent.createdBy?.firstName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        agent.createdBy?.lastName?.toLowerCase().includes(searchTerm.toLowerCase());
      
      const matchesInstitution = institutionFilter === 'all' || 
        agent.createdBy?.institution === institutionFilter;
      
      return matchesSearch && matchesInstitution;
    });
  }, [agents, searchTerm, institutionFilter]);

  const toggleRowExpansion = (agentId) => {
    const newExpanded = new Set(expandedRows);
    if (newExpanded.has(agentId)) {
      newExpanded.delete(agentId);
    } else {
      newExpanded.add(agentId);
    }
    setExpandedRows(newExpanded);
  };

  const handleDelete = async (agentId, agentName) => {
    if (window.confirm(`Are you sure you want to delete the agent "${agentName}"?`)) {
      try {
        await deleteAgent({ id: agentId });
        toast({
          title: "Agent deleted successfully",
          status: "success",
          duration: 3000,
          isClosable: true,
        });
        refetch();
      } catch (error) {
        toast({
          title: "Error deleting agent",
          description: error.toString(),
          status: "error",
          duration: 5000,
          isClosable: true,
        });
      }
    }
  };

  const canEdit = (agent) => {
    return isSystemAdmin || agent.createdBy?.id === user?.id;
  };

  const canDelete = (agent) => {
    return isSystemAdmin || agent.createdBy?.id === user?.id;
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (fetching && !agents) {
    return (
      <Container maxW="container.xl" py={6}>
        <Flex justify="center" align="center" minH="200px">
          <Spinner size="xl" />
        </Flex>
      </Container>
    );
  }

  if (error) {
    return (
      <Container maxW="container.xl" py={6}>
        <Alert status="error">
          <AlertIcon />
          Error loading agents: {error.toString()}
        </Alert>
      </Container>
    );
  }

  return (
    <Container maxW="container.xl" py={6}>
      <VStack spacing={6} align="stretch">
        
        {/* Header */}
        <Flex>
          <VStack align="start" spacing={1}>
            <Heading size="lg">VM Agents Management</Heading>
            <Text color="gray.600">
              Manage AI agents created through the VM system
            </Text>
          </VStack>
          <Spacer />
          <Button
            as={Link}
            to="/create-agent"
            leftIcon={<AddIcon />}
            colorScheme="blue"
            size="md"
          >
            Create Agent
          </Button>
        </Flex>

        {/* Filters */}
        <Card>
          <CardBody>
            <SimpleGrid columns={{ base: 1, md: 2, lg: 3 }} spacing={4}>
              <Box>
                <Text mb={2} fontSize="sm" fontWeight="medium">Search</Text>
                <Input
                  placeholder="Search by name, description, or creator..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  leftElement={<SearchIcon color="gray.400" />}
                />
              </Box>
              
              {isSystemAdmin && (
                <Box>
                  <Text mb={2} fontSize="sm" fontWeight="medium">Institution</Text>
                  <Select
                    value={institutionFilter}
                    onChange={(e) => setInstitutionFilter(e.target.value)}
                  >
                    <option value="all">All Institutions</option>
                    {institutions.map(institution => (
                      <option key={institution} value={institution}>
                        {institution}
                      </option>
                    ))}
                  </Select>
                </Box>
              )}
              
              <Box>
                <Text mb={2} fontSize="sm" fontWeight="medium">Results</Text>
                <Text color="gray.600">
                  {filteredAgents.length} agent{filteredAgents.length !== 1 ? 's' : ''} found
                </Text>
              </Box>
            </SimpleGrid>
          </CardBody>
        </Card>

        {/* Agents Table */}
        <Card>
          <CardBody p={0}>
            <Table variant="simple">
              <Thead>
                <Tr>
                  <Th></Th>
                  <Th>Agent Name</Th>
                  <Th>Status</Th>
                  <Th>Creator</Th>
                  {isSystemAdmin && <Th>Institution</Th>}
                  <Th>Created</Th>
                  <Th>Actions</Th>
                </Tr>
              </Thead>
              <Tbody>
                {filteredAgents.map((agent) => (
                  <React.Fragment key={agent.id}>
                    <Tr>
                      <Td w="50px">
                        <IconButton
                          size="sm"
                          variant="ghost"
                          icon={expandedRows.has(agent.id) ? <ChevronUpIcon /> : <ChevronDownIcon />}
                          onClick={() => toggleRowExpansion(agent.id)}
                          aria-label="Expand details"
                        />
                      </Td>
                      <Td>
                        <VStack align="start" spacing={1}>
                          <Text fontWeight="medium">{agent.name}</Text>
                          <Text fontSize="sm" color="gray.600" noOfLines={1}>
                            {agent.description || 'No description'}
                          </Text>
                        </VStack>
                      </Td>
                      <Td>
                        <Badge 
                          colorScheme={agent.isActive ? 'green' : 'gray'}
                          variant="subtle"
                        >
                          {agent.isActive ? 'Active' : 'Inactive'}
                        </Badge>
                      </Td>
                      <Td>
                        <VStack align="start" spacing={0}>
                          <Text fontSize="sm">
                            {agent.createdBy?.firstName} {agent.createdBy?.lastName}
                          </Text>
                          <Text fontSize="xs" color="gray.500">
                            {agent.createdBy?.email}
                          </Text>
                        </VStack>
                      </Td>
                      {isSystemAdmin && (
                        <Td>
                          <Text fontSize="sm">{agent.createdBy?.institution || 'N/A'}</Text>
                        </Td>
                      )}
                      <Td>
                        <Text fontSize="sm">
                          {formatDate(agent.createdAt)}
                        </Text>
                      </Td>
                      <Td>
                        <HStack spacing={2}>
                          <Tooltip label="Chat with agent">
                            <IconButton
                              size="sm"
                              variant="outline"
                              icon={<ChatIcon />}
                              onClick={() => navigate(`/agents/vm/${agent.id}`)}
                              aria-label="Chat with agent"
                            />
                          </Tooltip>
                          
                          <Tooltip label="View details">
                            <IconButton
                              size="sm"
                              variant="outline"
                              icon={<ViewIcon />}
                              onClick={() => toggleRowExpansion(agent.id)}
                              aria-label="View details"
                            />
                          </Tooltip>
                          
                          {canEdit(agent) && (
                            <Tooltip label="Edit agent">
                              <IconButton
                                size="sm"
                                variant="outline"
                                icon={<EditIcon />}
                                onClick={() => navigate(`/create-agent?edit=${agent.id}`)}
                                aria-label="Edit agent"
                              />
                            </Tooltip>
                          )}
                          
                          {canDelete(agent) && (
                            <Tooltip label="Delete agent">
                              <IconButton
                                size="sm"
                                variant="outline"
                                colorScheme="red"
                                icon={<DeleteIcon />}
                                onClick={() => handleDelete(agent.id, agent.name)}
                                isLoading={deleting}
                                aria-label="Delete agent"
                              />
                            </Tooltip>
                          )}
                        </HStack>
                      </Td>
                    </Tr>
                    
                    {/* Expanded Details */}
                    <Tr>
                      <Td colSpan={isSystemAdmin ? 7 : 6} p={0}>
                        <Collapse in={expandedRows.has(agent.id)}>
                          <Box p={4} bg="gray.50" borderTop="1px" borderColor="gray.200">
                            <SimpleGrid columns={{ base: 1, md: 2, lg: 3 }} spacing={4}>
                              
                              <Box>
                                <Text fontSize="sm" fontWeight="bold" mb={2}>Configuration</Text>
                                <VStack align="start" spacing={1}>
                                  <Text fontSize="xs">
                                    <strong>Version:</strong> {agent.version || 'N/A'}
                                  </Text>
                                  <Text fontSize="xs">
                                    <strong>Last Modified:</strong>{' '}
                                    {agent.lastModifiedBy ? 
                                      `${agent.lastModifiedBy.firstName} ${agent.lastModifiedBy.lastName}` : 
                                      'N/A'
                                    }
                                  </Text>
                                </VStack>
                              </Box>
                              
                              <Box>
                                <Text fontSize="sm" fontWeight="bold" mb={2}>Enabled Tools</Text>
                                <VStack align="start" spacing={1} maxH="100px" overflowY="auto">
                                  {agent.enabledTools && Array.isArray(agent.enabledTools) ? 
                                    agent.enabledTools.map((tool, index) => (
                                      <Tag key={index} size="sm" variant="outline">
                                        <TagLabel>{tool}</TagLabel>
                                      </Tag>
                                    )) : 
                                    <Text fontSize="xs" color="gray.500">No tools enabled</Text>
                                  }
                                </VStack>
                              </Box>
                              
                              <Box>
                                <Text fontSize="sm" fontWeight="bold" mb={2}>Model Settings</Text>
                                <Text fontSize="xs" color="gray.600" noOfLines={3}>
                                  {agent.modelSettings ? 
                                    JSON.stringify(agent.modelSettings, null, 2) : 
                                    'No model settings configured'
                                  }
                                </Text>
                              </Box>
                              
                            </SimpleGrid>
                            
                            <Divider my={3} />
                            
                            <Box>
                              <Text fontSize="sm" fontWeight="bold" mb={2}>Instructions</Text>
                              <Text fontSize="xs" color="gray.700" maxH="100px" overflowY="auto">
                                {agent.instructions?.markdown || 'No instructions provided'}
                              </Text>
                            </Box>
                          </Box>
                        </Collapse>
                      </Td>
                    </Tr>
                  </React.Fragment>
                ))}
              </Tbody>
            </Table>
            
            {filteredAgents.length === 0 && (
              <Box p={8} textAlign="center">
                <Text color="gray.500">
                  {agents?.length === 0 ? 
                    'No agents found. Create your first agent to get started.' :
                    'No agents match your current filters.'
                  }
                </Text>
                {agents?.length === 0 && (
                  <Button
                    as={Link}
                    to="/create-agent"
                    mt={4}
                    colorScheme="blue"
                    variant="outline"
                  >
                    Create Your First Agent
                  </Button>
                )}
              </Box>
            )}
          </CardBody>
        </Card>
        
      </VStack>
    </Container>
  );
};

export default VMAgentsManagement;